import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AfterViewInit, Component, OnInit, Signal, inject, DestroyRef } from "@angular/core";
import { SettingMappingModelService } from "@app/settings-components/settings-mapping-component/setting-mapping-model.service";
import * as Sentry from "@sentry/angular";

import { AppStartupTimingService } from "@app/shared/services";
import { IframeMessagesService } from "src/app/shared/iframe-messages-service/iframe-messages.service";
import { applicationStringFromApplicationType, productStringFromApplicationType } from "src/app/views-interfaces/ApplicationType.enum";
import { DashboardsService, FirmsService, ResponseErrorHandlerService } from "../../shared/";
import { CardViewService } from "../../shared/card-view-service/card-view.service";
import { HelpersService } from "../../shared/helpers-service/helpers.service";
import { IDashboardModel } from "../../views-interfaces/dashboard-model.interface";

import { DragAndDropService } from "@app/drag-and-drop/drag-and-drop.service";
import { NavBarComponent } from "../../navbar-component/navbar.component";
import { HeaderComponent } from "../../header-component/header.component";
import { RouterOutlet } from "@angular/router";
import { AppLoaderComponent } from "../../shared/components/app-loader/app-loader.component";

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: "app-view",
    templateUrl: "./view.component.html",
    styleUrl: "./view.component.scss",
    host: { class: "wk-view" },
    imports: [NavBarComponent, HeaderComponent, RouterOutlet, AppLoaderComponent],
})
export class ViewComponent implements OnInit, AfterViewInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly cardViewService = inject(CardViewService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly dragAndDropService = inject(DragAndDropService);
    private readonly firmsService = inject(FirmsService);
    private readonly helpersService = inject(HelpersService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly appStartupTimingService = inject(AppStartupTimingService);
    private readonly responseErrorHandler = inject(ResponseErrorHandlerService);
    private readonly settingMappingModelService = inject(SettingMappingModelService);

    showCardDownload = false;
    lang: string;

    bannerIsShown = false;
    isFirstLoad = false;
    protected dragging: Signal<boolean> = this.dragAndDropService.isDragging;
    currentDashboard: IDashboardModel;
    spinnerRequested = false;

    constructor() {
        this.lang = this.helpersService.getLang();

        // cardDownloadEvent$ is emitted synchronously by CardDisplayService the first time any
        // descendant triggers a card load. That can happen while this component's view is being
        // checked, which flips showCardDownload after the @if binding was read (NG0100). Applying
        // the value in a microtask moves the write past the current change detection pass.
        this.helpersService.cardDownloadEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
            void Promise.resolve().then(() => this.applyCardDownloadStatus(status));
        });

        // Subscribe to spinner requests for showing/hiding spinner
        this.helpersService.spinnerRequested$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
            this.spinnerRequested = status;
        });
    }

    private applyCardDownloadStatus(status: boolean) {
        this.showCardDownload = status;

        if (status !== false || !this.isFirstLoad) return;

        this.isFirstLoad = false;
        console.timeEnd("Application responsive elapsed time");

        // TODO : USE SENTRY SERVICE
        Sentry.startSpanManual(
            {
                name: "WebApp_Performance-ApplicationResponsive",
                op: "ApplicationResponsive",
                startTime: this.appStartupTimingService.getStartTime(),
            },
            async (span) => {
                span?.setAttribute("Site URL", this.iframeMessagesService.messagesOrigin());
                span?.setAttribute("Application", applicationStringFromApplicationType(this.currentDashboard.ApplicationType));
                span?.setAttribute("Product", productStringFromApplicationType(this.currentDashboard.ApplicationType));
                span?.setAttribute("Firm name", (await this.firmsService.getFirm())?.Name);
                span?.setAttribute("Dashboard title", this.currentDashboard.Title);
                span?.end();
            },
        );
    }

    ngOnInit() {
        this.spinnerRequested = false;
        if (this.cardViewService.settingsSectionChanged) {
            location.reload();
        }
        this.responseErrorHandler.showBannerEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.bannerIsShown = true;
        });

        this.responseErrorHandler.hideBannerEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.bannerIsShown = false;
        });
    }

    ngAfterViewInit() {
        this.dashboardsService.getCurrentDashboard().then(async (dashboard) => {
            this.currentDashboard = dashboard;
            this.isFirstLoad = true;
            await this.settingMappingModelService.init();
        });
    }
}
